/**
 * NotificationsTab — third tab of the consumer Profile page. Consolidates the
 * old standalone NotificationPreferencesPage into Profile so the user has one
 * settings surface.
 *
 * Three stacked cards in a single column (maxWidth 720):
 *   1. Channels — Email / SMS / Push (browser) / In-app toggles
 *   2. Categories — 6 per-category toggles (application updates, doc requests,
 *      messages, proposals, deadlines, Astor insights)
 *   3. Quiet hours — start/end time inputs + disclaimer
 *
 * All toggles auto-save via `useUpdateNotificationPreferences`. Push toggle
 * additionally calls into `PushNotificationContext` to request/revoke the
 * browser permission and FCM token registration (existing wiring carried
 * forward from the legacy ProfilePage).
 */

import {
  Box,
  GlassAlert,
  GlassCard,
  GlassSwitch,
  GlassTextField,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { usePushNotificationContext } from '../../contexts/PushNotificationContext';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '../../hooks/useNotifications';

interface ChannelRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  error?: string | null;
  isLast?: boolean;
}

function ChannelRow({ label, description, checked, onChange, disabled, error, isLast }: ChannelRowProps): ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.5,
        minHeight: 44,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
          {description}
        </Typography>
        {error && (
          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.25 }}>
            {error}
          </Typography>
        )}
      </Box>
      <GlassSwitch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
}

interface CategoryRowProps {
  label: string;
  subText: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  isLast?: boolean;
}

function CategoryRow({ label, subText, checked, onChange, disabled, isLast }: CategoryRowProps): ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.5,
        minHeight: 44,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
          {subText}
        </Typography>
      </Box>
      <GlassSwitch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
}

const CATEGORY_KEYS = [
  {
    id: 'case_updates',
    labelKey: 'consumer.profile.notifications.catApplicationUpdates',
    subKey: 'consumer.profile.notifications.catApplicationUpdatesSub',
  },
  {
    id: 'document_requests',
    labelKey: 'consumer.profile.notifications.catDocRequests',
    subKey: 'consumer.profile.notifications.catDocRequestsSub',
  },
  {
    id: 'messages',
    labelKey: 'consumer.profile.notifications.catMessages',
    subKey: 'consumer.profile.notifications.catMessagesSub',
  },
  {
    id: 'proposals',
    labelKey: 'consumer.profile.notifications.catProposals',
    subKey: 'consumer.profile.notifications.catProposalsSub',
  },
  {
    id: 'deadlines',
    labelKey: 'consumer.profile.notifications.catDeadlines',
    subKey: 'consumer.profile.notifications.catDeadlinesSub',
  },
  {
    id: 'astor_insights',
    labelKey: 'consumer.profile.notifications.catAstorInsights',
    subKey: 'consumer.profile.notifications.catAstorInsightsSub',
  },
] as const;

export function NotificationsTab(): ReactElement {
  const { t } = useI18n();
  const push = usePushNotificationContext();
  const { data: prefsData } = useNotificationPreferences();
  const updatePrefs = useUpdateNotificationPreferences();

  // Local mirror for snappy toggle UX; falls back to server state.
  const eventPrefs = (prefsData?.eventPreferences ?? {}) as Record<string, boolean>;
  const prefs = useMemo(
    () => ({
      emailEnabled: prefsData?.emailEnabled ?? true,
      smsEnabled: prefsData?.smsEnabled ?? false,
      pushEnabled: prefsData?.pushEnabled ?? false,
      inAppEnabled: prefsData?.inAppEnabled ?? true,
      quietHoursStart: prefsData?.quietHoursStart ?? '22:00',
      quietHoursEnd: prefsData?.quietHoursEnd ?? '08:00',
    }),
    [prefsData],
  );

  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const flashSaved = useCallback(() => {
    setSavedFlash(t('notification.preferences.saved'));
    setTimeout(() => setSavedFlash(null), 2500);
  }, [t]);

  const handleChannelChange = useCallback(
    (key: 'emailEnabled' | 'smsEnabled' | 'inAppEnabled', value: boolean) => {
      updatePrefs.mutate({ [key]: value } as Partial<typeof prefs>, {
        onSuccess: flashSaved,
      });
    },
    [updatePrefs, flashSaved],
  );

  const handlePushChange = useCallback(
    async (value: boolean) => {
      if (value) {
        const granted = await push.requestPermission();
        if (!granted) return;
      } else {
        await push.revokePermission();
      }
      updatePrefs.mutate({ pushEnabled: value }, { onSuccess: flashSaved });
    },
    [push, updatePrefs, flashSaved],
  );

  const handleCategoryChange = useCallback(
    (categoryId: string, value: boolean) => {
      updatePrefs.mutate({ eventPreferences: { ...eventPrefs, [categoryId]: value } }, { onSuccess: flashSaved });
    },
    [updatePrefs, flashSaved, eventPrefs],
  );

  const handleQuietHoursChange = useCallback(
    (key: 'quietHoursStart' | 'quietHoursEnd', value: string) => {
      updatePrefs.mutate({ [key]: value } as Partial<typeof prefs>, { onSuccess: flashSaved });
    },
    [updatePrefs, flashSaved],
  );

  const pushError = !push.isSupported
    ? t('profile.pushNotificationsUnsupported')
    : push.permissionState === 'denied'
      ? t('profile.pushNotificationsDenied')
      : push.error;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, maxWidth: 720, mx: 'auto' }}>
      {savedFlash && (
        <GlassAlert severity="success" sx={{ mb: 0 }}>
          {savedFlash}
        </GlassAlert>
      )}
      {updatePrefs.error && (
        <GlassAlert severity="error" sx={{ mb: 0 }}>
          {updatePrefs.error.message}
        </GlassAlert>
      )}

      {/* ───── Channels ───── */}
      <GlassCard sx={{ p: 2 }}>
        <Typography variant="overline" color="textSecondary" sx={{ display: 'block', mb: 1, letterSpacing: '0.08em' }}>
          {t('consumer.profile.notifications.channelsHeading')}
        </Typography>
        <ChannelRow
          label={t('consumer.profile.notifications.channelEmail')}
          description={t('notification.preferences.channelEmailDescription')}
          checked={prefs.emailEnabled}
          onChange={(v) => handleChannelChange('emailEnabled', v)}
        />
        <ChannelRow
          label={t('consumer.profile.notifications.channelSms')}
          description={t('notification.preferences.channelSmsDescription')}
          checked={prefs.smsEnabled}
          onChange={(v) => handleChannelChange('smsEnabled', v)}
        />
        <ChannelRow
          label={t('consumer.profile.notifications.channelPush')}
          description={t('consumer.profile.notifications.devicesEnrolled', { count: 2 })}
          checked={prefs.pushEnabled}
          onChange={handlePushChange}
          disabled={!push.isSupported || push.permissionState === 'denied' || push.isLoading}
          error={pushError}
        />
        <ChannelRow
          label={t('consumer.profile.notifications.channelInApp')}
          description={t('consumer.profile.notifications.inAppAlways')}
          checked={prefs.inAppEnabled}
          onChange={(v) => handleChannelChange('inAppEnabled', v)}
          isLast
        />
      </GlassCard>

      {/* ───── Categories ───── */}
      <GlassCard sx={{ p: 2 }}>
        <Typography
          variant="overline"
          color="textSecondary"
          sx={{ display: 'block', mb: 0.5, letterSpacing: '0.08em' }}
        >
          {t('consumer.profile.notifications.categoriesHeading')}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
          {t('consumer.profile.notifications.categoriesSubtitle')}
        </Typography>
        {CATEGORY_KEYS.map((cat, idx) => (
          <CategoryRow
            key={cat.id}
            label={t(cat.labelKey)}
            subText={t(cat.subKey)}
            checked={eventPrefs[cat.id] ?? true}
            onChange={(v) => handleCategoryChange(cat.id, v)}
            isLast={idx === CATEGORY_KEYS.length - 1}
          />
        ))}
      </GlassCard>

      {/* ───── Quiet hours ───── */}
      <GlassCard sx={{ p: 2 }}>
        <Typography variant="overline" color="textSecondary" sx={{ display: 'block', mb: 1, letterSpacing: '0.08em' }}>
          {t('consumer.profile.notifications.quietHoursHeading')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <GlassTextField
            label={t('consumer.profile.notifications.quietHoursStart')}
            type="time"
            value={prefs.quietHoursStart}
            onChange={(e) => handleQuietHoursChange('quietHoursStart', e.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <GlassTextField
            label={t('consumer.profile.notifications.quietHoursEnd')}
            type="time"
            value={prefs.quietHoursEnd}
            onChange={(e) => handleQuietHoursChange('quietHoursEnd', e.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          {t('consumer.profile.notifications.quietHoursDisclaimer')}
        </Typography>
      </GlassCard>
    </Box>
  );
}

export default NotificationsTab;
